import { useEffect } from "react";
import * as React from "react";
import {
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import VolumeUp from "@mui/icons-material/VolumeUp";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import Input from "@mui/material/Input";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

type SliderFormFieldProps = {
  id?: string;
  testId?: string;
  label?: string;
  name: string;
  disabled?: boolean;
  required?: boolean;
  sx?: SxProps | null;
  isFieldValid?: boolean;
  color?: string;
  onFieldValidation?: (name: string, isValid: boolean) => void;

  afterUpdate?: (value: string | number) => void;
};

export type RhfSliderFormFieldProps<T extends FieldValues> =
  SliderFormFieldProps & UseControllerProps<T>;

export default function SliderFormField<T extends FieldValues>({
  id,
  testId,
  name,
  disabled = false,
  control,
  label,
  rules = {},
  required = false,
  isFieldValid = true,
  onFieldValidation,
  afterUpdate,
  color,
  sx
}: RhfSliderFormFieldProps<T>) {
  const {
    field: { onChange, onBlur, value, ...field },
    fieldState: { error, invalid }
  } = useController<T>({
    name,
    control,
    rules: {
      ...rules,
      ...(required
        ? {
            required: { value: true, message: `Value is required` }
          }
        : {})
    }
  });
  const isValid = isFieldValid && !Boolean(invalid);
  const errorMessage = error?.message;

  useEffect(() => {
    if (onFieldValidation) {
      onFieldValidation(name, isValid);
    }
  }, [isValid, onFieldValidation, name]);

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    const value = String(newValue || "0");
    onChange(value);
    afterUpdate?.(value);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === "" ? 0 : String(event.target.value);
    onChange(value);
    afterUpdate?.(value);
  };

  const handleBlur = () => {
    if (value < 0) {
      onChange("0");
      afterUpdate?.("0");
    } else if (value > 100) {
      onChange("100");
      afterUpdate?.("100");
    }
  };

  return (
    <FormControl
      disabled={disabled}
      required={required}
      fullWidth
      error={Boolean(errorMessage)}
      sx={sx}
    >
      {label && (
        <Typography
          gutterBottom
          sx={{ color, textTransform: "uppercase", fontWeight: 700 }}
        >
          {label}
        </Typography>
      )}

      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <VolumeUp htmlColor={color} />
        </Grid>
        <Grid item xs>
          <Slider
            disabled={disabled}
            value={isNaN(parseInt(value)) ? 0 : Number(value)}
            onChange={handleSliderChange}
            sx={{ color }}
          />
        </Grid>
        <Grid item>
          <Input
            value={value}
            size="small"
            onChange={handleInputChange}
            onBlur={handleBlur}
            inputProps={{
              step: 1,
              min: 0,
              max: 100,
              type: "number",
              "aria-labelledby": "input-slider"
            }}
            sx={{ borderRadius: 2 }}
            {...field}
          />
        </Grid>
      </Grid>

      {Boolean(errorMessage) && (
        <FormHelperText error={Boolean(errorMessage)}>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
}
